import { captureException } from '@sentry/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { useClient } from '@web/api';
import {
  ArticleImage,
  DeliveryEstimate,
  ItemPrice,
  LoadingSpinner,
  useParameters,
} from '@web/utils';
import { AddToCartButton } from '@/components/AddToCartButton';
import { env } from '@/env';
import {
  InventoryDocument,
  InventoryQuery,
  InventoryQueryVariables,
} from '@/hooks/inventoryItem.generated';
import { useAppSelector } from '@/store/hooks';
import { LivePrice } from '@/types';
import { getItemDataFromArticle } from '@/utils/getItemDataFromArticle';
import { ArticleData, CartItem } from '@agrantis/cart';
import { populateCartItem } from '@agrantis/cart/cartHelpers';
import { getArticlePriceData } from '@agrantis/cart/getArticlePriceData';
import { getCustomerPrice } from '@agrantis/cart/getCustomerPrice';

export interface InventoryCatalogItemProps {
  article: ArticleData;
  onItemOpen?: (item: CartItem) => void;
  isVolumeDiscountsEnabled: boolean;
  livePrices: LivePrice[];
  livePricesFetching: boolean;
}

const GridArticle = (props: InventoryCatalogItemProps) => {
  const {
    article,
    isVolumeDiscountsEnabled,
    livePrices,
    livePricesFetching,
    onItemOpen,
  } = props;
  const { retailerId } = useParameters<{ retailerId: string }>();
  const { t } = useTranslation();
  const settings = useAppSelector((state) => state.retailer.retailer?.settings);
  const livePriceErpType = settings?.livePriceErpType;
  const user = useAppSelector((state) => state.user.data);
  const customerPrice = getCustomerPrice(article.customerPrice);
  const priceTables = settings?.priceTables ?? null;

  // live prices below
  const livePrice = livePrices.find(
    (price) => price.inventoryId === article.item.id,
  );

  const isPriceLoading = useMemo(() => {
    if (!livePriceErpType) return false;
    if (livePrice) return false;
    if (livePricesFetching) return true;

    return false;
  }, [livePriceErpType, livePrice, livePricesFetching]);

  const hideVolumeDiscounts =
    !isVolumeDiscountsEnabled ||
    livePrice?.price !== undefined ||
    isPriceLoading;

  const shouldShowVolumeDiscount =
    isVolumeDiscountsEnabled &&
    customerPrice === null &&
    article.volumeDiscounts?.length &&
    article.volumeDiscounts.length > 0 &&
    !hideVolumeDiscounts;

  const client = useClient();

  const getInventoryItemDataRetailerBasePriceAmount = async () => {
    if (!shouldShowVolumeDiscount) return null;
    try {
      const { data } = await client.query<
        InventoryQuery,
        InventoryQueryVariables
      >(InventoryDocument, {
        inventoryId: article.item.id,
        retailerId: retailerId || '',
      });
      return data?.inventory.retailerBasePrice?.amount;
    } catch (error) {
      captureException(error);
      return null;
    }
  };

  const openItem = async () => {
    if (!onItemOpen) return;

    const retailerBasePriceAmount =
      await getInventoryItemDataRetailerBasePriceAmount();

    const { finalPrice } = getArticlePriceData({
      item: article,
      livePrice,
      retailerPriceTables: priceTables,
      customerGroupId: user?.erpCustomerGroupId,
    });

    const item = populateCartItem(
      getItemDataFromArticle(
        article,
        !hideVolumeDiscounts,
        !!livePrice,
        retailerBasePriceAmount,
      ),
      {
        amount: 1,
        price: finalPrice,
      },
    );
    onItemOpen(item);
  };

  const hasSale = !!article.item.salePrice && article.item.salePrice > 0;

  return (
    <div className="border border-primary-border rounded-10-0 flex relative overflow-hidden flex-col">
      {!!hasSale && (
        <div className="sale absolute right-0 top-0 bg-[#E62525] px-1-0 py-0-5">
          <p className="text-primary-white font-bd text-2-2 md:text-1-2 leading-1-76">
            {t('inventory.sale')}
          </p>
        </div>
      )}
      {!hasSale && shouldShowVolumeDiscount && (
        <div className="sale absolute right-0 top-0 bg-[#E62525] px-1-0 py-0-5">
          <p className="text-primary-white font-bd text-2-2 md:text-1-2 leading-1-76">
            Mengenrabatt
          </p>
        </div>
      )}
      <div className="flex-col p-3-0 md:p-2-0 flex">
        <div>
          <p className="line-clamp-2 text-2-0 md:text-1-2 text-primary-black mb-2-0 md:mb-1-0">
            {article.taxonomy.name}
          </p>
          <div>
            <p className={clsx('text-2-2 md:text-1-4 font-bd min-h-[3.6rem]')}>
              {article.name}
            </p>
            <p className="text-2-0 md:text-1-2">{article.subtitle}</p>
          </div>
        </div>
        <div className={clsx('relative h-18-0 md:h-9-0 my-3-0 md:my-2-0 ')}>
          <ArticleImage
            image={article.image ?? ''}
            taxonomyId={article.taxonomy.id}
            env={env}
            className="mx-auto max-h-100-percent rounded-1-0 overflow-hidden"
          />
        </div>
      </div>
      <div
        className={clsx(
          article.description && 'border-t',
          'border-primary-border px-3-0 md:px-2-0 py-1-0 border-b',
        )}
      >
        <p className="text-2-0 md:text-1-2 min-h-[4.8rem] line-clamp-3  ">
          {article.description}
        </p>
      </div>
      <div className={'p-3-0 md:p-2-0'}>
        <div className={clsx('flex items-center mb-4-5 md:mb-2-5')}>
          <DeliveryEstimate t={t} article={article} />
        </div>
        <div
          className={clsx(
            'flex-row flex justify-between items-start min-h-[3rem]',
          )}
        >
          {isPriceLoading ? (
            <LoadingSpinner className="flex justify-center py-2-0" />
          ) : (
            <>
              <ItemPrice article={article} livePrice={livePrice} />
              <AddToCartButton
                handleAddToCartPress={openItem}
                article={article}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GridArticle;
